import React, { memo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DealForm } from 'site-modules/lease-deals/components/deals-checker-widget/components/deal-form/deal-form';
import { DealResult } from 'site-modules/lease-deals/components/deals-checker-widget/components/deal-result/deal-result';
import { calculateDealData } from 'site-modules/lease-deals/components/deals-checker-widget/utils/calculate-deal-data';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { LeaseDealCheckerModel, LeaseDealCheckerPaths } from 'client/data/models/lease-deal-checker';

import './deal-checker.scss';

const getVehicleKey = ({ makeName, modelName, year }) => `${makeName}-${modelName}-${year}`;

const DealCheckerUI = memo(({ dealScore = null, setModelValue }) => {
  const [dealData, setDealData] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState(null);

  const handleSubmit = useCallback(
    data => {
      const calculatedData = calculateDealData(data);

      if (calculatedData === null) {
        setDealData(null);
        setVehicleInfo(null);
        return;
      }

      const { makeName, modelName, year } = data;
      const vehicleKey = getVehicleKey({ makeName, modelName, year });

      setDealData(prevDealData => {
        if (prevDealData?.vehicleKey === vehicleKey) {
          return {
            ...calculatedData,
            vehicleKey,
            bodyType: prevDealData.bodyType,
            effectivePaymentMedian: prevDealData.effectivePaymentMedian,
          };
        }

        return { ...calculatedData, vehicleKey: null };
      });
      setVehicleInfo({ makeName, modelName, year });

      setModelValue(
        LeaseDealCheckerPaths.getDealScorePath({ make: makeName, model: modelName, year }),
        LeaseDealCheckerModel,
        { make: makeName, model: modelName, year }
      );
    },
    [setModelValue]
  );

  useEffect(() => {
    if (!dealScore || !vehicleInfo) return;

    const { makeName, modelName, year } = vehicleInfo;
    const vehicleKey = getVehicleKey({ makeName, modelName, year });

    const apiData = dealScore?.[makeName]?.[modelName]?.[year];

    if (!apiData) return;

    const { bodyType = null, effectivePaymentMedian = null } = apiData;

    setDealData(prevDealData => {
      if (prevDealData.bodyType === bodyType && prevDealData.effectivePaymentMedian === effectivePaymentMedian) {
        return prevDealData;
      }

      return {
        ...prevDealData,
        vehicleKey,
        bodyType,
        effectivePaymentMedian,
      };
    });
  }, [dealScore, vehicleInfo]);

  return (
    <div className="deal-checker p-2 rounded-24 d-flex flex-column flex-md-row w-100 row-gap-2 bg-white">
      <DealForm onSubmit={handleSubmit} />
      <hr className="vr mx-2 my-0 bg-cool-gray-70 hidden-md-down" />
      <DealResult dealData={dealData} />
    </div>
  );
});

DealCheckerUI.displayName = 'DealCheckerUI';

DealCheckerUI.propTypes = {
  dealScore: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      [PropTypes.string]: PropTypes.shape({
        [PropTypes.string]: PropTypes.shape({
          bodyType: PropTypes.string,
          effectivePaymentMedian: PropTypes.number,
        }),
      }),
    }),
  }),
  setModelValue: PropTypes.func.isRequired,
};

const stateToPropsConfig = {
  dealScore: bindToPath('dealScore', LeaseDealCheckerModel),
};

export const DealChecker = connectToModel(DealCheckerUI, stateToPropsConfig);
